import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import RestaurantLayout from '../layouts/RestaurantLayout';
import { useAuth } from '../contexts/AuthContext';
import AuthGuard from '../contexts/AuthGuard';

const Dashboard = lazy(() => import('../pages/modules/restaurant/dashboard/Dashboard'));
const FOS = lazy(() => import('../pages/modules/restaurant/user-management/FOS/FOS'));
const Retailers = lazy(() => import('../pages/modules/restaurant/user-management/Retailers/Retailers'));
const Restaurant = lazy(() => import('../pages/modules/restaurant/user-management/Restaurant/Restaurant'));
const BranchManagement = lazy(() => import('../pages/modules/restaurant/user-management/BranchManagement/BranchManagement'));
const BranchTree = lazy(() => import('../pages/modules/restaurant/user-management/BranchTree/BranchTree'));
const KitchenManagement = lazy(() => import('../pages/modules/restaurant/user-management/KitchenManagement/KitchenManagement'));
const DeliveryManagement = lazy(() => import('../pages/modules/restaurant/user-management/DeliveryManagement/DeliveryManagement'));
const CashierManagement = lazy(() => import('../pages/modules/restaurant/user-management/CashierManagement/CashierManagement'));
const CustomerManagement = lazy(() => import('../pages/modules/restaurant/user-management/CustomerManagement/CustomerManagement'));
const MenuTree = lazy(() => import('../pages/modules/restaurant/menu-management/MenuTree/MenuTree'));
const MenuCategory = lazy(() => import('../pages/modules/restaurant/menu-management/MenuCategory/MenuCategory'));
const MenuSubcategory = lazy(() => import('../pages/modules/restaurant/menu-management/MenuSubcategory/MenuSubcategory'));
const Section = lazy(() => import('../pages/modules/restaurant/menu-management/Section/Section'));
const DiningTables = lazy(() => import('../pages/modules/restaurant/menu-management/DiningTables/DiningTables'));
const MenuItems = lazy(() => import('../pages/modules/restaurant/menu-management/MenuItems/MenuItems'));
const MenuItemsBulkUpdate = lazy(() => import('../pages/modules/restaurant/menu-management/MenuItems/MenuItemsBulkUpdate'));
const Addons = lazy(() => import('../pages/modules/restaurant/menu-management/Addons/Addons'));
const DeliveryZones = lazy(() => import('../pages/modules/restaurant/menu-management/DeliveryZones/DeliveryZones'));
const RestaurantHours = lazy(() => import('../pages/modules/restaurant/menu-management/RestaurantHours/RestaurantHours'));

// Order Management Components
const OrderList = lazy(() => import('../pages/modules/restaurant/orders/OrderList/OrderList'));
const NewOrders = lazy(() => import('../pages/modules/restaurant/orders/NewOrders/NewOrders'));
const Preparing = lazy(() => import('../pages/modules/restaurant/orders/Preparing/Preparing'));
const Onway = lazy(() => import('../pages/modules/restaurant/orders/Onway/Onway'));
const Delivered = lazy(() => import('../pages/modules/restaurant/orders/Delivered/Delivered'));
const CancelledOrders = lazy(() => import('../pages/modules/restaurant/orders/CancelledOrders/CancelledOrders'));

// Settings Components
const Slider = lazy(() => import('../pages/modules/restaurant/settings/Slider/Slider'));
const Coupons = lazy(() => import('../pages/modules/restaurant/settings/Coupons/Coupons'));
const BankDetail = lazy(() => import('../pages/modules/restaurant/settings/BankDetail/BankDetail'));
const MarqueeMessages = lazy(() => import('../pages/modules/restaurant/settings/MarqueeMessages/MarqueeMessages'));
const BusinessSettings = lazy(() => import('../pages/modules/admin/settings/BusinessSettings/BusinessSettings'));
const BranchBusinessOverrides = lazy(() => import('../pages/modules/restaurant/settings/BranchBusinessOverrides/BranchBusinessOverrides'));
const PaymentGateway = lazy(() => import('../pages/modules/restaurant/settings/PaymentGateway/PaymentGateway'));
const MySubscription = lazy(() => import('../pages/modules/restaurant/subscription/MySubscription'));

// Outstanding Components
const OutstandingDelivery = lazy(() => import('../pages/modules/restaurant/outstanding/Delivery/OutstandingDelivery'));
const OutstandingHistory = lazy(() => import('../pages/modules/restaurant/outstanding/OutstandingHistory/OutstandingHistory'));

// Wallet Topup
const WalletTopupRequest = lazy(() => import('../pages/modules/restaurant/wallet-topup-request/WalletTopupRequest'));
const TopupHistory = lazy(() => import('../pages/modules/restaurant/wallet-topup-request/TopupHistory'));

// Withdrawals
const WithdrawalRequests = lazy(() => import('../pages/modules/restaurant/withdrawals/WithdrawalRequests'));

// Shared operations
const CustomerList = lazy(() => import('../pages/modules/shared/CustomerList/CustomerList'));

const RouteFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
  </div>
);

const RestaurantRoutes = () => {
  const { logout } = useAuth();

  return (
    <AuthGuard>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<RestaurantLayout onLogout={logout} />}>
          <Route path="/" element={<Navigate to="/restaurant/dashboard" replace />} />
          <Route path="/restaurant" element={<Navigate to="/restaurant/dashboard" replace />} />

          {/* Restaurant Module Routes */}
          <Route path="/restaurant/dashboard" element={<Dashboard />} />
          <Route path="/restaurant/user-management/fos" element={<FOS />} />
          <Route path="/restaurant/user-management/retailers" element={<Retailers />} />
          <Route path="/restaurant/user-management/restaurants" element={<Restaurant />} />
          <Route path="/restaurant/user-management/branches" element={<BranchManagement />} />
          <Route path="/restaurant/user-management/branch-tree" element={<BranchTree />} />
          <Route path="/restaurant/user-management/kitchen" element={<KitchenManagement />} />
          <Route path="/restaurant/user-management/delivery" element={<DeliveryManagement />} />
          <Route path="/restaurant/user-management/cashier" element={<CashierManagement />} />
          <Route path="/restaurant/user-management/customers" element={<CustomerManagement />} />
          <Route path="/restaurant/operations/customers" element={<CustomerList scope="restaurant" />} />
          <Route path="/restaurant/menu-management/menu-tree" element={<MenuTree />} />
          <Route path="/restaurant/menu-management/categories" element={<MenuCategory />} />
          <Route path="/restaurant/menu-management/subcategories" element={<MenuSubcategory />} />
          <Route path="/restaurant/menu-management/sections" element={<Section />} />
          <Route path="/restaurant/menu-management/dining-tables" element={<DiningTables />} />
          <Route path="/restaurant/menu-management/items" element={<MenuItems />} />
          <Route path="/restaurant/user-management/items-bulk-update" element={<MenuItemsBulkUpdate />} />
          <Route path="/restaurant/menu-management/items-bulk-update" element={<MenuItemsBulkUpdate />} />
          <Route path="/restaurant/menu-management/addons" element={<Addons />} />
          <Route path="/restaurant/user-management/delivery-zones" element={<DeliveryZones />} />
          <Route path="/restaurant/user-management/restaurant-hours" element={<RestaurantHours />} />

          {/* Outstanding Routes */}
          <Route path="/restaurant/outstanding/delivery" element={<OutstandingDelivery />} />
          <Route path="/restaurant/outstanding/history" element={<OutstandingHistory />} />

          {/* Wallet Topup */}
          <Route path="/restaurant/wallet-topup/requests" element={<WalletTopupRequest />} />
          <Route path="/restaurant/wallet-topup/history" element={<TopupHistory />} />

          {/* Withdrawals */}
          <Route path="/restaurant/withdrawals/requests" element={<WithdrawalRequests />} />

          {/* Order Management Routes */}
          <Route path="/restaurant/orders/list" element={<OrderList />} />
          <Route path="/restaurant/orders/new" element={<NewOrders />} />
          <Route path="/restaurant/orders/preparing" element={<Preparing />} />
          <Route path="/restaurant/orders/onway" element={<Onway />} />
          <Route path="/restaurant/orders/delivered" element={<Delivered />} />
          <Route path="/restaurant/orders/cancelled" element={<CancelledOrders />} />

          {/* Settings Routes */}
          <Route path="/restaurant/settings/business-settings" element={<BusinessSettings />} />
          <Route path="/restaurant/settings/branch-overrides" element={<BranchBusinessOverrides />} />
          <Route path="/restaurant/settings/slider" element={<Slider />} />
          <Route path="/restaurant/settings/coupons" element={<Coupons />} />
          <Route path="/restaurant/settings/bank-details" element={<BankDetail />} />
          <Route path="/restaurant/settings/marquee-messages" element={<MarqueeMessages />} />
          <Route path="/restaurant/settings/payment-gateway" element={<PaymentGateway />} />

          {/* Subscription */}
          <Route path="/restaurant/subscription" element={<MySubscription />} />

          <Route path="*" element={<Navigate to="/restaurant/dashboard" replace />} />
        </Route>
      </Routes>
      </Suspense>
    </AuthGuard>
  );
};

export default RestaurantRoutes;
